import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/axiosConfig';
import CarpetaPersonal from '../CarpetaPersonal/CarpetaPersonal';
import './Entidades.css';

function BuscadorSelect({ items, value, onChange, query, setQuery, open, setOpen, displayLabel, displayMeta, placeholder }) {
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = items.find(i => i.id === Number(value));

  const normalize = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filtered = query.trim()
    ? items.filter(i => normalize(displayLabel(i)).includes(normalize(query)) || normalize(displayMeta(i)).includes(normalize(query)))
    : items;

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [setOpen]);

  if (selected && !open) {
    return (
      <div className="buscador-select" ref={wrapRef}>
        <div className="buscador-select-tag" onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}>
          <i className="bi bi-check-circle-fill" style={{ color: 'var(--primary)', fontSize: '16px' }}></i>
          <div className="buscador-select-tag-info">
            <span className="buscador-select-tag-label">{displayLabel(selected)}</span>
            <span className="buscador-select-tag-meta">{displayMeta(selected)}</span>
          </div>
          <button type="button" className="buscador-select-cambiar" onClick={(e) => { e.stopPropagation(); setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}>
            Cambiar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="buscador-select" ref={wrapRef}>
      <div className="buscador-select-input-wrap">
        <i className="bi bi-search"></i>
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          placeholder={placeholder || "Buscar..."}
          value={query}
          autoFocus
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && (
        <div className="buscador-select-dropdown">
          {filtered.length === 0 ? (
            <div className="buscador-select-empty">Sin resultados</div>
          ) : (
            filtered.slice(0, 50).map(i => (
              <div key={i.id} className="buscador-select-option" onClick={() => { onChange(String(i.id)); setQuery(''); setOpen(false); }}>
                <span className="buscador-select-option-label">{displayLabel(i)}</span>
                <span className="buscador-select-option-meta">{displayMeta(i)}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const Entidades = ({ onImpersonate, isAdmin, entidadCuit, addToast }) => {
  const [activeTab, setActiveTab] = useState('directorio');
  const [entidades, setEntidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entidadSeleccionada, setEntidadSeleccionada] = useState(null);
  const [esVistaPrevia, setEsVistaPrevia] = useState(false);
  const [activeRoleFilter, setActiveRoleFilter] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('entidades_viewMode') || 'grid';
  });

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('entidades_viewMode', mode);
  };

  const [pinnedIds, setPinnedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('entidades_pinned') || '[]');
    } catch { return []; }
  });

  const togglePin = (entidadId) => {
    setPinnedIds(prev => {
      const next = prev.includes(entidadId)
        ? prev.filter(id => id !== entidadId)
        : [entidadId, ...prev];
      localStorage.setItem('entidades_pinned', JSON.stringify(next));
      return next;
    });
  };

  const [formRazonSocial, setFormRazonSocial] = useState('');
  const [formCuit, setFormCuit] = useState('');
  const [formRol, setFormRol] = useState('');
  const [creando, setCreando] = useState(false);

  const [relaciones, setRelaciones] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [formRelUsuario, setFormRelUsuario] = useState('');
  const [formRelEntidad, setFormRelEntidad] = useState('');
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [searchRelacion, setSearchRelacion] = useState('');
  const [relUserQuery, setRelUserQuery] = useState('');
  const [relUserOpen, setRelUserOpen] = useState(false);
  const [relEntQuery, setRelEntQuery] = useState('');
  const [relEntOpen, setRelEntOpen] = useState(false);


  const handleSelectEntidad = (entidad, modoVista = 'archivos') => {
    setEntidadSeleccionada(entidad);
    setEsVistaPrevia(modoVista === 'vistaPrevia');
  };

  const handleVolverDirectorio = () => {
    setEntidadSeleccionada(null);
    setEsVistaPrevia(false);
  };

  useEffect(() => {
    const fetchEntidades = async () => {
      try {
        const res = await api.get('/obtenerTodasLasEntidades');
        const data = res.data.entidades || res.data.data || res.data || [];
        
        if (data.length > 0) {
          setEntidades(data);
        } else {
          throw new Error("No hay entidades todavia o formato inesperado");
        }
      } catch (error) {
        console.error("Error al traer entidades", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEntidades();
  }, []);

  useEffect(() => {
    const fetchRelaciones = async () => {
      try {
        const res = await api.get('/obtenerRelaciones');
        setRelaciones(res.data.data || []);
      } catch (error) {
        console.error('Error al obtener relaciones:', error);
      }
    };
    fetchRelaciones();
  }, []);

  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const res = await api.post('/obtenerUsuarios');
        setUsuarios(res.data || []);
      } catch (error) {
        console.error('Error al obtener usuarios:', error);
      }
    };
    fetchUsuarios();
  }, []);

  const handleCrearRelacion = async (e) => {
    e.preventDefault();
    if (!formRelUsuario || !formRelEntidad) {
      addToast('Selecciona un usuario y una entidad', 'error');
      return;
    }
    try {
      await api.post('/crearRelacion', {
        usuarioId: Number(formRelUsuario),
        entidadId: Number(formRelEntidad)
      });
      addToast('Relacion creada correctamente');
      setFormRelUsuario('');
      setFormRelEntidad('');
      const res = await api.get('/obtenerRelaciones');
      setRelaciones(res.data.data || []);
    } catch (error) {
      const msg = error.response?.data?.message || 'Error al crear la relacion';
      addToast(msg, 'error');
    }
  };

  const handleEliminarRelacion = async (relacion) => {
    if (!window.confirm(`¿Eliminar la relacion de ${relacion.usuario_nombre} con ${relacion.entidad_nombre}?`)) return;
    try {
      await api.delete('/eliminarRelacion', {
        data: { usuarioId: relacion.usuario_id, entidadId: relacion.entidad_id }
      });
      addToast('Relacion eliminada');
      setRelaciones(prev => prev.filter(r => r.usuario_id !== relacion.usuario_id || r.entidad_id !== relacion.entidad_id));
    } catch (error) {
      addToast('Error al eliminar la relacion', 'error');
    }
  };

  const handleCrearEntidad = async (e) => {
    e.preventDefault();
    if (!formRazonSocial || !formCuit || !formRol) {
      addToast('Completa todos los campos', 'error');
      return;
    }
    setCreando(true);
    try {
      await api.post('/crearEntidad', {
        razonSocial: formRazonSocial,
        cuit: formCuit,
        rolNombre: formRol
      });

      addToast('Entidad creada correctamente');
      setFormRazonSocial('');
      setFormCuit('');
      setFormRol('');
      setActiveTab('directorio');
      setLoading(true);
      const res = await api.get('/obtenerTodasLasEntidades');
      setEntidades(res.data.data || res.data || []);
    } catch (error) {
      const msg = error.response?.data?.message || 'Error al crear la entidad';
      addToast(msg, 'error');
    } finally {
      setCreando(false);
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="entidades-loading">Cargando directorio de entidades...</div>;
  }

  if (entidadSeleccionada) {
    return (
      <div className="entidad-carpeta-view">
        <div className="volver-bar">
          <button className="btn-volver-directorio" onClick={handleVolverDirectorio}>
            <i className="bi bi-arrow-left"></i> Volver al Directorio de Entidades
          </button>
        </div>
        <CarpetaPersonal 
          carpetaId={entidadSeleccionada.carpeta_id} 
          nombreCliente={entidadSeleccionada.nombre || entidadSeleccionada.razonSocial}
          isAdmin={!esVistaPrevia}
          entidadCuit={entidadSeleccionada.cuit}
          addToast={addToast}
        />
      </div>
    );
  }

  const predefinedRoles = [
    'Monotributista',
    'Autonomo',
    'Persona juridica',
    'Sociedad de hecho',
    'Dependiente',
    'Asociacion sin fines de lucro'
  ];
  
  const normalize = (r) => r.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const seen = new Set();
  const allRoles = [];
  predefinedRoles.forEach(r => {
    const key = normalize(r);
    if (!seen.has(key)) { seen.add(key); allRoles.push(r); }
  });
  entidades.forEach(e => {
    if (e.rol && normalize(e.rol) !== 'cliente') {
      const key = normalize(e.rol);
      if (!seen.has(key)) { seen.add(key); allRoles.push(e.rol); }
    }
  });
  const availableRoles = ['Todos', ...allRoles];
  
  const filteredByRole = activeRoleFilter === 'Todos' 
    ? entidades 
    : entidades.filter(e => normalize(e.rol || 'Sin Rol') === normalize(activeRoleFilter));

  const filteredEntidades = filteredByRole.filter(e => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const nombre = (e.nombre || e.razonSocial || '').toLowerCase();
    const cuit = (e.cuit || '').toLowerCase();
    return nombre.includes(term) || cuit.includes(term);
  });

  const sortedEntidades = [...filteredEntidades].sort((a, b) => {
    const aPinned = pinnedIds.includes(a.id);
    const bPinned = pinnedIds.includes(b.id);
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    return 0;
  });

  const getRoleColor = (rol) => {
    switch(rol?.toLowerCase()) {
      case 'monotributista': return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' };
      case 'autonomo': return { bg: '#fef08a', text: '#854d0e', border: '#fde047' };
      case 'persona juridica': return { bg: '#e0e7ff', text: '#3730a3', border: '#c7d2fe' };
      case 'sociedad de hecho': return { bg: '#ffedd5', text: '#9a3412', border: '#fed7aa' };
      case 'dependiente': return { bg: '#f3e8ff', text: '#6b21a8', border: '#e9d5ff' };
      case 'asociacion sin fines de lucro': return { bg: '#ffe4e6', text: '#9f1239', border: '#fecdd3' };
      case 'todos': return { bg: '#1a3c5e', text: '#ffffff', border: '#0d1b2a' };
      default: return { bg: '#f1f5f9', text: '#475569', border: '#e2e8f0' };
    }
  };

  function renderDirectorio() {
    if (entidades.length === 0) {
      return (
        <div className="entidades-empty-state" style={{ marginTop: '32px' }}>
          <div className="entidades-empty-icon">
            <i className="bi bi-building"></i>
          </div>
          <h3>No hay entidades registradas</h3>
          <p>Todavía no se ha creado ninguna entidad en el sistema.</p>
          <button className="btn btn-primary entidades-empty-btn" onClick={() => setActiveTab('nueva')}>
            <i className="bi bi-plus-lg"></i> Crear Primera Entidad
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="entidades-header">
          <div className="entidades-header-top">
            <div>
              <h2><i className="bi bi-building" style={{ marginRight: 8, color: '#1a3c5e' }}></i>Directorio de Entidades</h2>
              <p>Selecciona una entidad para visualizar y administrar su carpeta personal de archivos.</p>
            </div>
            <div className="entidades-search">
              <i className="bi bi-search"></i>
              <input 
                type="text" 
                placeholder="Buscar por nombre o CUIT..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="entidades-filters-row">
            <div className="entidades-role-filters">
              {availableRoles.map(rol => {
                const isActive = activeRoleFilter === rol;
                const roleColors = getRoleColor(rol);
                return (
                  <button
                    key={rol}
                    className={`role-filter-btn ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveRoleFilter(rol)}
                    style={isActive ? {
                      backgroundColor: roleColors.bg,
                      color: roleColors.text,
                      borderColor: roleColors.border
                    } : {}}
                  >
                    {rol}
                  </button>
                );
              })}
            </div>
            <div className="entidades-view-toggle">
              <button className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => handleViewModeChange('grid')} title="Vista cuadricula">
                <i className="bi bi-grid-3x3-gap-fill"></i>
              </button>
              <button className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => handleViewModeChange('table')} title="Vista lista">
                <i className="bi bi-list-ul"></i>
              </button>
            </div>
          </div>
        </div>

        {viewMode === 'grid' ? (
          <div className="entidades-grid">
            {sortedEntidades.length === 0 ? (
              <div className="entidades-empty-state">
                <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '16px' }}></i>
                <p>No se encontraron entidades que coincidan con tu busqueda</p>
                <button className="btn-limpiar-filtros" onClick={() => { setSearchTerm(''); setActiveRoleFilter('Todos'); }}>
                  <i className="bi bi-x-circle"></i> Limpiar filtros
                </button>
              </div>
            ) : (
              sortedEntidades.map((entidad, index) => {
                const displayNombre = entidad.nombre || entidad.razonSocial || 'Entidad';
                return (
                  <div 
                    key={index} 
                    className="entidad-card"
                    style={entidad.rol ? { borderTop: `4px solid ${getRoleColor(entidad.rol).border}` } : {}}
                  >
                    <button
                      className={`entidad-pin-btn ${pinnedIds.includes(entidad.id) ? 'pinned' : ''}`}
                      onClick={(e) => { e.stopPropagation(); togglePin(entidad.id); }}
                      title={pinnedIds.includes(entidad.id) ? 'Desfijar' : 'Fijar al inicio'}
                    >
                      <i className={`bi ${pinnedIds.includes(entidad.id) ? 'bi-pin-angle-fill' : 'bi-pin-angle'}`}></i>
                    </button>
                    {entidad.rol && (
                      <div 
                        className="entidad-rol-badge-top"
                        style={{
                          backgroundColor: getRoleColor(entidad.rol).bg,
                          color: getRoleColor(entidad.rol).text
                        }}
                      >
                        {entidad.rol}
                      </div>
                    )}
                    
                    <div className="entidad-header-info">
                      <div className="entidad-avatar" style={entidad.rol ? { backgroundColor: getRoleColor(entidad.rol).text } : {}}>
                        {displayNombre.charAt(0).toUpperCase()}
                      </div>
                      <div className="entidad-datos">
                        <h3 className="entidad-nombre">{displayNombre}</h3>
                        {entidad.cuit && <p className="entidad-cuit" title={entidad.cuit}>Cuit: {entidad.cuit}</p>}
                      </div>
                    </div>

                    <div className="entidad-footer-actions">
                      <button 
                        className="btn-abrir-carpeta primary-action"
                        onClick={() => handleSelectEntidad(entidad, 'archivos')}
                        title={`Abrir carpeta de ${displayNombre}`}
                      >
                        <i className="bi bi-folder"></i>
                        Archivos
                      </button>
                      {onImpersonate && (
                        <button 
                          className="btn-abrir-carpeta secondary-action btn-impersonar"
                          onClick={() => { setEsVistaPrevia(true); onImpersonate({ ...entidad, nombre: displayNombre, rol: entidad.rol || 'cliente' }); }}
                          title={`Ver sistema como ${displayNombre}`}
                        >
                          <i className="bi bi-eye"></i>
                          Ver Sesion
                        </button>
                      )}
                    </div>

                  </div>
                )
              })
            )}
          </div>
        ) : (
          <div className="entidades-table-wrap">
            <table className="entidades-table">
              <thead>
                <tr>
                  <th>Entidad</th>
                  <th>CUIT</th>
                  <th>Rol</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedEntidades.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <div className="entidades-empty-state" style={{ border: 'none', boxShadow: 'none' }}>
                        <i className="bi bi-inbox" style={{ fontSize: '2rem', color: '#cbd5e1', marginBottom: '8px' }}></i>
                        <p>No se encontraron entidades</p>
                        <button className="btn-limpiar-filtros" onClick={() => { setSearchTerm(''); setActiveRoleFilter('Todos'); }}>
                          <i className="bi bi-x-circle"></i> Limpiar filtros
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  sortedEntidades.map((entidad, index) => {
                    const displayNombre = entidad.nombre || entidad.razonSocial || 'Entidad';
                    const colors = getRoleColor(entidad.rol);
                    return (
                      <tr key={index} className="entidad-table-row">
                        <td>
                          <div className="entidad-table-cell-name">
                            <div className="entidad-table-avatar" style={entidad.rol ? { backgroundColor: colors.text } : {}}>
                              {displayNombre.charAt(0).toUpperCase()}
                            </div>
                            <span>{displayNombre}</span>
                          </div>
                        </td>
                        <td className="entidad-table-cuit">{entidad.cuit || '-'}</td>
                        <td>
                          {entidad.rol && (
                            <span className="entidad-table-rol-badge" style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}>
                              {entidad.rol}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="entidad-table-actions">
                            <button
                              className={`entidad-table-pin-btn ${pinnedIds.includes(entidad.id) ? 'pinned' : ''}`}
                              onClick={() => togglePin(entidad.id)}
                              title={pinnedIds.includes(entidad.id) ? 'Desfijar' : 'Fijar al inicio'}
                            >
                              <i className={`bi ${pinnedIds.includes(entidad.id) ? 'bi-pin-angle-fill' : 'bi-pin-angle'}`}></i>
                            </button>
                            <button className="entidad-table-btn" onClick={() => handleSelectEntidad(entidad, 'archivos')} title="Abrir carpeta">
                              <i className="bi bi-folder2-open"></i> Archivos
                            </button>
                            {onImpersonate && (
                              <button className="entidad-table-btn secundario" onClick={() => { setEsVistaPrevia(true); onImpersonate({ ...entidad, nombre: displayNombre, rol: entidad.rol || 'cliente' }); }} title="Ver sesion">
                                <i className="bi bi-eye"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  function renderRelaciones() {
    return (
      <div className="entidades-section">
        <div className="section-header-custom">
          <h3><i className="bi bi-diagram-3"></i> Relaciones Usuario-Entidad</h3>
        </div>

        <form className="relacion-form" onSubmit={handleCrearRelacion}>
          <div className="relacion-form-row">
            <div className="form-group-dash relacion-searchable">
              <label>Usuario</label>
              <BuscadorSelect
                items={usuarios}
                value={formRelUsuario}
                onChange={setFormRelUsuario}
                query={relUserQuery}
                setQuery={setRelUserQuery}
                open={relUserOpen}
                setOpen={setRelUserOpen}
                displayLabel={u => `${u.nombre}${u.cuit ? ` (${u.cuit})` : ''}`}
                displayMeta={u => u.rol || ''}
                placeholder="Buscar usuario..."
              />
            </div>
            <div className="form-group-dash relacion-searchable">
              <label>Entidad</label>
              <BuscadorSelect
                items={entidades}
                value={formRelEntidad}
                onChange={setFormRelEntidad}
                query={relEntQuery}
                setQuery={setRelEntQuery}
                open={relEntOpen}
                setOpen={setRelEntOpen}
                displayLabel={e => `${e.razonSocial || e.nombre}`}
                displayMeta={e => e.cuit || ''}
                placeholder="Buscar entidad..."
              />
            </div>
            <div className="relacion-btn-wrap">
              <button type="submit" className="btn btn-primary">
                <i className="bi bi-link-45deg"></i> Vincular
              </button>
            </div>
          </div>
        </form>

        <div className="relaciones-search">
          <i className="bi bi-search"></i>
          <input
            type="text"
            className="form-control"
            placeholder="Buscar por usuario o entidad..."
            value={searchRelacion}
            onChange={(e) => {
              setSearchRelacion(e.target.value);
              if (e.target.value.trim()) {
                const term = e.target.value.toLowerCase();
                const toExpand = new Set();
                relaciones.forEach(r => {
                  if (r.usuario_nombre.toLowerCase().includes(term) ||
                      r.entidad_nombre.toLowerCase().includes(term) ||
                      (r.entidad_cuit && r.entidad_cuit.toLowerCase().includes(term))) {
                    toExpand.add(String(r.usuario_id));
                  }
                });
                setExpandedUsers(toExpand);
              }
            }}
          />
          {searchRelacion && (
            <button className="relaciones-search-clear" onClick={() => { setSearchRelacion(''); setExpandedUsers(new Set()); }}>
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>

        <div className="relaciones-tree-wrap">
          {relaciones.length === 0 ? (
            <div className="admin-proc-empty"><p>No hay relaciones creadas.</p></div>
          ) : (
            (() => {
              const grupos = {};
              const term = searchRelacion.toLowerCase().trim();
              const filtered = term
                ? relaciones.filter(r =>
                    r.usuario_nombre.toLowerCase().includes(term) ||
                    r.entidad_nombre.toLowerCase().includes(term) ||
                    (r.entidad_cuit && r.entidad_cuit.toLowerCase().includes(term))
                  )
                : relaciones;
              filtered.forEach(r => {
                if (!grupos[r.usuario_id]) {
                  grupos[r.usuario_id] = { nombre: r.usuario_nombre, email: r.usuario_email, entidades: [] };
                }
                grupos[r.usuario_id].entidades.push(r);
              });
              return Object.entries(grupos).map(([userId, grupo]) => {
                const isExpanded = expandedUsers.has(userId);
                const toggle = () => {
                  setExpandedUsers(prev => {
                    const next = new Set(prev);
                    if (next.has(userId)) next.delete(userId);
                    else next.add(userId);
                    return next;
                  });
                };
                const lastIdx = grupo.entidades.length - 1;
                return (
                  <div key={userId} className={`tree-node ${isExpanded ? 'expanded' : ''}`}>
                    <div className="tree-node-header" onClick={toggle}>
                      <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} tree-chevron`}></i>
                      <i className="bi bi-person-fill tree-user-icon"></i>
                      <div className="tree-node-info">
                        <span className="tree-node-name">{grupo.nombre}</span>
                        <span className="tree-node-meta">{grupo.email}</span>
                      </div>
                      <span className="tree-badge">{grupo.entidades.length}</span>
                    </div>
                    {isExpanded && (
                      <div className="tree-children">
                        {grupo.entidades.map((r, idx) => (
                          <div key={`${r.usuario_id}-${r.entidad_id}`} className="tree-child-row">
                            <div className="tree-connector">
                              <span className="tree-line"></span>
                              {idx === lastIdx ? <span className="tree-corner"></span> : <span className="tree-branch"></span>}
                            </div>
                            <i className="bi bi-building tree-entity-icon"></i>
                            <div className="tree-child-info">
                              <span className="tree-child-name">{r.entidad_nombre}</span>
                              <span className="tree-child-meta">{r.entidad_cuit}</span>
                            </div>
                            <button className="tree-remove-btn" onClick={() => handleEliminarRelacion(r)} title="Eliminar">
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              });
            })()
          )}
        </div>
      </div>
    );
  }

  function renderNuevaEntidad() {
    return (
      <div className="entidades-section-center">
        <div className="entidad-form-wrap">
          {creando ? (
            <div className="entidad-creando-overlay">
              <div className="entidad-spinner"></div>
              <p>Creando entidad y generando estructura de carpetas...</p>
            </div>
          ) : (
            <>
              <div className="entidad-form-header">
                <i className="bi bi-plus-circle"></i>
                <h3>Nueva Entidad</h3>
                <p>Completa los datos para registrar una nueva entidad en el sistema</p>
              </div>
              <form className="entidad-form" onSubmit={handleCrearEntidad}>
                <div className="form-group-dash">
                  <label><i className="bi bi-building"></i> Razon Social / Nombre</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Nombre o razon social de la entidad"
                    value={formRazonSocial}
                    onChange={(e) => setFormRazonSocial(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group-dash">
                  <label><i className="bi bi-upc-scan"></i> CUIT</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="XX-XXXXXXXX-X"
                    value={formCuit}
                    onChange={(e) => setFormCuit(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group-dash">
                  <label><i className="bi bi-tag"></i> Rol</label>
                  <select
                    className="form-control"
                    value={formRol}
                    onChange={(e) => setFormRol(e.target.value)}
                    required
                  >
                    <option value="">Seleccionar rol...</option>
                    {predefinedRoles.map((rol) => (
                      <option key={rol} value={rol}>{rol}</option>
                    ))}
                  </select>
                </div>
                <div className="entidad-form-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setActiveTab('directorio')}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={creando}>
                    <i className="bi bi-check-lg"></i> Crear Entidad
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="entidades-container">
      <div className="entidades-tabs">
        <button className={`entidades-tab ${activeTab === 'directorio' ? 'active' : ''}`} onClick={() => setActiveTab('directorio')}>
          <i className="bi bi-building"></i> Directorio
        </button>
        <button className={`entidades-tab ${activeTab === 'nueva' ? 'active' : ''}`} onClick={() => setActiveTab('nueva')}>
          <i className="bi bi-plus-circle"></i> Nueva Entidad
        </button>
        <button className={`entidades-tab ${activeTab === 'relaciones' ? 'active' : ''}`} onClick={() => setActiveTab('relaciones')}>
          <i className="bi bi-diagram-3"></i> Relaciones
        </button>
      </div>

      {activeTab === 'directorio' && renderDirectorio()}
      {activeTab === 'nueva' && renderNuevaEntidad()}
      {activeTab === 'relaciones' && renderRelaciones()}
    </div>
  );
};

export default Entidades;
